import React, { useState, useCallback, useEffect, useRef } from 'react';
import { FiSettings, FiEye, FiEyeOff } from 'react-icons/fi';
import { Column } from './DataTable';

interface ColumnSettingsProps {
  columns: Column[];
  storageKey: string;
  onColumnsChange: (visibleKeys: string[]) => void;
}

const ColumnSettings: React.FC<ColumnSettingsProps> = ({ columns, storageKey, onColumnsChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const savedKeys = localStorage.getItem(`col-${storageKey}`);
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(() => {
    if (savedKeys) {
      try {
        return new Set(JSON.parse(savedKeys));
      } catch { /* ignore */ }
    }
    return new Set(columns.map(c => c.key));
  });

  useEffect(() => {
    onColumnsChange(Array.from(visibleKeys));
    localStorage.setItem(`col-${storageKey}`, JSON.stringify(Array.from(visibleKeys)));
  }, [visibleKeys, storageKey, onColumnsChange]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = useCallback((key: string) => {
    setVisibleKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        if (next.size <= 2) return next; // keep at least 2 columns
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const showAll = useCallback(() => {
    setVisibleKeys(new Set(columns.map(c => c.key)));
  }, [columns]);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        className="btn btn-secondary btn-sm"
        onClick={() => setOpen(!open)}
        title="Konfiguracja kolumn"
        style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
      >
        <FiSettings size={14} />
        <span style={{ fontSize: '11px' }}>Kolumny</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: 0,
          zIndex: 1000,
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          borderRadius: '8px',
          boxShadow: 'var(--shadow-lg)',
          padding: '8px 0',
          minWidth: '220px',
          maxHeight: '400px',
          overflow: 'auto',
        }}>
          <div style={{ padding: '4px 12px 8px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text)' }}>Widoczne kolumny</span>
            <button
              className="btn btn-link btn-sm"
              onClick={showAll}
              style={{ fontSize: '10px', padding: '2px 6px' }}
            >
              Pokaż wszystkie
            </button>
          </div>
          {columns.map(col => {
            const isVisible = visibleKeys.has(col.key);
            return (
              <div
                key={col.key}
                onClick={() => toggle(col.key)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: isVisible ? 'var(--text)' : 'var(--text-muted)',
                  background: isVisible ? 'transparent' : 'var(--hover)',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--hover)'}
                onMouseLeave={(e) => e.currentTarget.style.background = isVisible ? 'transparent' : 'var(--hover)'}
              >
                {isVisible ? <FiEye size={14} style={{ color: 'var(--primary)' }} /> : <FiEyeOff size={14} />}
                <span style={{ fontWeight: isVisible ? 500 : 400 }}>{col.header}</span>
              </div>
            );
          })}
          <div style={{ padding: '6px 12px', borderTop: '1px solid var(--border)', fontSize: '10px', color: 'var(--text-muted)' }}>
            {visibleKeys.size} z {columns.length} kolumn widocznych
          </div>
        </div>
      )}
    </div>
  );
};

export default ColumnSettings;
